/**
 * 작성자 : 박승호
 * 날짜 : 2024.08.12
 * 경로 : 카메라 폰트 색상 컴포넌트
 */

import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Root = styled.div`
  .color-pallete {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    cursor: pointer;
    flex-shrink: 0;

    &.selected {
      .btn-box {
        color: var(--text-reverse) !important;
        background-color: var(--primary) !important;
        &:active {
          color: var(--text-reverse) !important;
          background-color: var(--filled-blue-deep) !important;
        }
      }

      .btn-text {
        font-size: 0.875rem;
        font-weight: bold;
        color: var(--primary) !important;
      }
    }
    &.unselected:hover {
      .btn-box {
        background-color: var(--tonal-deep) !important;
      }
    }

    .btn-box {
      border-radius: 0.25rem;
      padding: 0.45rem 0.725rem;
      background-color: var(--tonal);

      &.small {
        height: 2rem;
        padding: 0.5rem;
      }
      &.medium {
        height: 2.25rem;
        padding: 0.75rem;
      }
      &.minWidth {
        min-width: 2.25rem;
      }
      &.width40 {
        min-width: 4rem;
      }
      &.width60 {
        min-width: 6rem;
      }
      &.width80 {
        min-width: 8rem;
      }
      .material-symbols-rounded {
        font-size: 1.375rem;
      }

      &.filledIcon {
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1;
        }
      }

      .name {
        letter-spacing: normal;
        font-size: 0.875rem;
        width: fit-content;
        word-break: keep-all;
        white-space: nowrap;
      }
      &.play_arrow_reverse {
        .material-symbols-rounded {
          transform: rotate(180deg);
        }
      }
      &.negative {
        color: var(--filled-red);
        background-color: var(--tonal-red);
        .name {
          font-weight: 600;
        }
        &:hover {
          background-color: var(--tonal-deep-red);
        }
        &:active {
          color: var(--filled-red-deep);
        }
      }
      &.darkModeOnly {
        color: var(--zinc-200);
        background-color: var(--zinc-800);
        .name {
          font-weight: 400;
        }
        &.selected,
        &:hover {
          color: var(--blue-100);
          background-color: var(--blue-700);
          &:active {
            color: var(--blue-100);
            background-color: var(--blue-600);
          }
        }
        &.unselected:hover {
          color: var(--zinc-100);
          background-color: var(--zinc-600);
        }
        &.negative {
          color: var(--red-500);
          &:hover {
            color: var(--red-100);
            background-color: var(--red-700);
          }
          &:active {
            color: var(--red-100);
            background-color: var(--red-600);
          }
        }
      }
      &.iconWeight_300 {
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 300;
        }
      }
    }

    .btn-text {
      font-size: 0.875rem;
      color: var(--zinc-400);
    }
  }
`;

interface ICameraFontColors {
  camFColor: string;
  setState: Dispatch<SetStateAction<any>>;
  className?: string;
  minWidth?: boolean;
  darkModeOnly?: boolean;
  iconWeight?: 400 | 300;
  filledIcon?: boolean;
  width?: 'width40' | 'width60' | 'width80';
}

const CameraFontColors = ({ camFColor, setState, className, minWidth, darkModeOnly, iconWeight, filledIcon, width }: ICameraFontColors) => {
  const { t } = useTranslation();
  // 폰트 색상 Options 생성
  const fontColorOptions = [
    { key: '255/255/13', icon: 'format_color_text', val: '255/255/13', color: '#FFFF0D', name: '노랑' },
    { key: '0/176/80', icon: 'format_color_text', val: '0/176/80', color: '#00B050', name: '초록' },
    { key: '255/102/0', icon: 'format_color_text', val: '255/102/0', color: '#FF6600', name: '주황' },
    { key: '191/191/191', icon: 'format_color_text', val: '191/191/191', color: '#BFBFBF', name: '회색' },
    { key: '0/112/191', icon: 'format_color_text', val: '0/112/191', color: '#0070BF', name: '파랑' },
    { key: '255/0/0', icon: 'format_color_text', val: '255/0/0', color: '#FF0000', name: '빨강' },
    { key: '255/255/255', icon: 'format_color_text', val: '255/255/255', color: '#FFFFFF', name: '흰색' },
  ];

  const setClassName = className ?? '';
  const setMinWidth = minWidth ? 'minWidth' : '';
  const setDarkModeOnly = darkModeOnly ? 'darkModeOnly' : '';
  const setIconWeight = iconWeight ? `iconWeight_${iconWeight}` : '';
  const setFilledIcon = filledIcon ? 'filledIcon' : '';
  const setWidth = width ?? '';

  return (
    <Root>
      <div className='color-pallete'>
        {fontColorOptions?.map((item) => {
          const defaultSelect = camFColor || fontColorOptions[0].key;
          const isSelected: boolean = defaultSelect === item.key;
          return (
            <div
              key={item.key}
              role='presentation'
              style={{ color: item.color }}
              className={`button ${isSelected ? 'selected' : 'unselected'}`}
              onClick={() => setState && setState((prev: any) => ({ ...prev, camFColor: item.key }))}
            >
              <div className={`btn-box ${setClassName} ${setMinWidth} ${setDarkModeOnly} ${setIconWeight} ${setFilledIcon} ${setWidth}`}>
                {item.icon && <span className='material-symbols-rounded'>{item.icon}</span>}
              </div>
              {item.name && <span className='btn-text'>{t(item.name)}</span>}
            </div>
          );
        })}
      </div>
    </Root>
  );
};

export default React.memo(CameraFontColors);
